import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { DictType } from '@pma/types'
import {
  PRIORITY_DICTIONARY_ENDPOINT,
  SERVER_URL,
  USER_STATUS_DICTIONARY_ENDPOINT,
} from '@pma/utils/api-routes'
import { parseCookies } from 'nookies'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import { PlusCircleIcon } from 'lucide-react'
import { Button } from '@components/common/button'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@components/common/popover'
import { useRef, useState } from 'react'
import { Input } from '@components/common/input'
import { Separator } from '@components/common/separator'
import { toast } from '@hooks/use-toast'
import { HexColorPicker } from 'react-colorful'
import { useAuth } from '@stores/auth-store'
import useTranslator from '@hooks/use-translator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../common/dropdown-menu'

interface Props {
  option: 'task' | 'project' | 'user'
  revalidate?: CallableFunction
}
const AddPriority = ({ option, revalidate }: Props) => {
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [isOpen, setIsOpen] = useState(false)
  const [priority, setPriority] = useState<DictType>({
    priority_name: '',
    color: '#6465ca',
    level: 1,
  })

  const colorPickerRef = useRef<HTMLDivElement | null>(null)

  const { translate } = useTranslator()
  const { authConfig } = useAuth()

  const orgId = authConfig?.last_organization_id

  const userId = authConfig?.id

  const forProject = option === 'project'
  const forUser = option === 'user'
  const forTask = option === 'task'

  const addPriority = async (payload: DictType) => {
    const ENDPOINT =
      option === 'user'
        ? USER_STATUS_DICTIONARY_ENDPOINT + '/add'
        : PRIORITY_DICTIONARY_ENDPOINT + '/add'
    const response = await axios.post(SERVER_URL + ENDPOINT, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response?.data
  }

  const addPriorityMutation = useMutation({
    mutationKey: ['add-priority', option],
    mutationFn: (payload: DictType) => addPriority(payload),
    onSuccess: (data) => {
      revalidate && revalidate()
      toast({
        title: translate('Success'),
        description: translate('Priority added successfully'),
        variant: 'success',
      })

      setPriority({})
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate('Error'),
        description: errorMessage || translate('Error adding priority'),
        variant: 'error',
      })
    },
  })

  const onSubmit = () => {
    if (!priority?.priority_name) {
      return toast({
        title: translate('Info'),
        description: `${translate('Enter task title')} ${priority?.priority_name}`,
        variant: 'info',
      })
    }

    const base = {
      ...priority,
      organization_id: orgId,
      user_id: userId,
      is_active: true,
    }

    const payload =
      forTask || forProject
        ? {
            name: priority?.priority_name,
            canceled: false,
            finished: false,
            for_task: forTask,
            for_project: forProject,
          }
        : {}

    return addPriorityMutation.mutate({
      ...base,
      ...payload,
    })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={'ghost'} size={'icon'} className='h-6 w-6 rounded-full'>
          <PlusCircleIcon className='text-primary' size={20} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className=''>
        <div className='space-y-2'>
          <h4 className='font-semibold'>
            {translate('Add new')} {translate(option)} {translate('priority')}
          </h4>
          <Separator />
          <div className='flex w-full flex-col gap-2' onMouseEnter={() => setIsOpen(false)}>
            <h3 className='text-xs font-semibold'>{translate('Name')}</h3>

            <Input
              placeholder={translate('Priority')}
              className='w-full'
              required
              onChange={(e: any) => setPriority((prev) => ({ ...prev, priority_name: e.target.value }))}
            />

            <h3 className='text-xs font-semibold'>{translate('Level')}</h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  className='flex h-9 justify-start px-3 py-1 text-black'
                >
                  <span>{priority.level}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start'>
                {Array.from(Array(4).keys()).map((val) => {
                  return (
                    <DropdownMenuItem
                      onSelect={(e: any) =>
                        setPriority((prev) => ({ ...prev, level: e.target.textContent }))
                      }
                    >
                      {val + 1}
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {option !== 'user' && (
              <>
                <h3 className='text-xs font-semibold'>{translate('Background Color')}</h3>
                <div className='relative'>
                  <Button
                    className='w-full'
                    style={{
                      background: priority?.color || 'blue',
                    }}
                    onClick={() => setIsOpen(true)}
                  />
                  {isOpen && (
                    <div className='absolute' ref={colorPickerRef}>
                      <HexColorPicker
                        className=''
                        defaultValue={priority?.color}
                        onChange={(e: any) => setPriority((prev) => ({ ...prev, color: e }))}
                      />
                    </div>
                  )}
                </div>

                <Input
                  type='text'
                  pattern='^#+([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$'
                  placeholder='#0000ff'
                  value={priority?.color}
                  onChange={(e: any) => setPriority((prev) => ({ ...prev, color: e.target.value }))}
                />
              </>
            )}
          </div>
        </div>
        <PopoverClose className='w-full'>
          <Button
            className='my-2 w-full'
            size={'sm'}
            onMouseEnter={() => setIsOpen(false)}
            onClick={onSubmit}
          >
            {translate('Add')}{' '}
          </Button>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  )
}

export default AddPriority
